// Template keys and the condition end markers are non-editable chips. A click
// on one leaves the browser caret inside its text, where Backspace either does
// nothing on a key or, through Jodit's own handling, eats a character of the
// chip. Chips behave like tokens instead: a click selects the whole chip, and
// Backspace or Delete with the caret caught inside removes it. The condition
// opener and the operators are editable and left to the browser.
const KEY_SELECTOR = '[data-template-key]';
const DELETION_KEYS = ['Backspace', 'Delete'];

const isFrozen = (element) =>
  element.getAttribute('contenteditable') === 'false';

/**
 * Finds the chip a click or a caret belongs to: the outermost non-editable
 * chip around the node. A key inside the editable condition opener is its own
 * token, so removing it leaves the opener in place.
 * @function
 *
 * @param {Node} node - The clicked or caret node.
 * @param {HTMLElement} root - The editor root.
 * @returns {HTMLElement|null} The chip to treat as a token, if any.
 */
export const tokenFor = (node, root) => {
  const element = node.nodeType === 3 ? node.parentElement : node;
  const chip = element?.closest?.(KEY_SELECTOR);
  if (!chip || !root.contains(chip) || !isFrozen(chip)) return null;

  let outermost = chip;
  let ancestor = chip.parentElement?.closest(KEY_SELECTOR);
  while (ancestor && root.contains(ancestor) && isFrozen(ancestor)) {
    outermost = ancestor;
    ancestor = ancestor.parentElement?.closest(KEY_SELECTOR);
  }
  return outermost;
};

const selectionOf = (editor) => {
  const selection = editor.ed?.getSelection?.() || globalThis.getSelection?.();
  return selection && selection.rangeCount ? selection : null;
};

const selectToken = (editor, token) => {
  const range = editor.ed.createRange();
  range.selectNode(token);
  editor.s.selectRange(range);
};

const onClick = (editor) => (event) => {
  const selection = selectionOf(editor);
  // A drag made a real selection: leave it alone.
  if (!selection || !selection.isCollapsed) return;
  const token = tokenFor(event.target, editor.editor);
  if (token) selectToken(editor, token);
};

const onKeyDown = (editor) => (event) => {
  if (!DELETION_KEYS.includes(event.key)) return;
  const selection = selectionOf(editor);
  if (!selection) return;
  const range = selection.getRangeAt(0);

  let token = null;
  if (range.collapsed) {
    token = tokenFor(range.startContainer, editor.editor);
  } else if (
    range.startContainer === range.endContainer &&
    range.endOffset - range.startOffset === 1
  ) {
    const selected = range.startContainer.childNodes[range.startOffset];
    token = selected && tokenFor(selected, editor.editor);
    if (token !== selected) token = null;
  }
  if (!token) return;

  event.preventDefault();
  const caret = editor.ed.createRange();
  caret.setStartBefore(token);
  caret.collapse(true);
  token.remove();
  editor.s.selectRange(caret);
  editor.synchronizeValues();
};

/**
 * Makes the non-editable chips of the editor behave like tokens. Bound once
 * per editor.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @returns {void}
 */
export const bindTemplateKeyInteractions = (editor) => {
  const root = editor?.editor;
  if (!root || root.dataset.templateKeyInteractionsBound === 'true') return;

  root.addEventListener('click', onClick(editor));
  root.addEventListener('keydown', onKeyDown(editor));
  root.dataset.templateKeyInteractionsBound = 'true';
};
